import { ComponentRef, DestroyRef, inject, Injectable, Injector, NgZone, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AuthenticationService } from './authentication.service';
import { ModalService } from 'app/core-lib/dialogs/modal/modal.service';
import { IdleNoticeComponent } from '../core-lib/alerts/idle-notice/idle-notice.component';
import { CoreLibSessionExpiredModalComponent } from 'app/core-lib/dialogs/session-expired-modal/session-expired-modal.component';
import { LogoutReason } from './session/logout-reason';
import { SESSION_OVERLAY_BACKDROP_CLASSES, SESSION_OVERLAY_PANEL_CLASSES } from './session/session-overlay';
import { SessionTerminationService } from './session/session-termination.service';

const LAST_ACTIVITY_KEY = 'idleLastActivityAt';
const TIMEOUT_CLAIM = 'session_timeout_minutes';
const WARNING_MS = 30_000;
const TICK_MS = 1_000;

const NAVIGATION_GRACE_MS = 5_000;
const NOTICE_PANEL_CLASSES = [...SESSION_OVERLAY_PANEL_CLASSES, 'idle-notice-panel'];

@Injectable({ providedIn: 'root' })
export class IdleSessionService implements OnDestroy {
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly overlay = inject(Overlay);
  private readonly injector = inject(Injector);
  private readonly modalService = inject(ModalService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly sessionTermination = inject(SessionTerminationService);
  private readonly jwtHelper = new JwtHelperService();

  private timeoutMs: number | null = null;
  private lastActivityAt = 0;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private navigationSub: Subscription | null = null;
  private overlayRef: OverlayRef | null = null;
  private noticeRef: ComponentRef<IdleNoticeComponent> | null = null;
  private isLoggingOut = false;
  private armedAt = 0;
  private storageUnavailable = false;

  constructor() {
    this.authenticationService
      .loggedOut()
      .pipe(takeUntilDestroyed(inject(DestroyRef)))
      .subscribe(() => this.stop());
  }

  start(): void {
    this.stop();
    this.isLoggingOut = false;
    this.storageUnavailable = false;
    const minutes = this.readClaimMinutes();
    if (minutes === null) return;
    this.timeoutMs = minutes * 60_000;

    const stored = this.readStoredActivity();
    if (stored !== null && Date.now() - stored >= this.timeoutMs) {
      this.isLoggingOut = true;
      this.sessionTermination.end(LogoutReason.Idle, { revoke: true });
      return;
    }

    this.lastActivityAt = stored ?? Date.now();
    this.writeStoredActivity(this.lastActivityAt);
    this.armedAt = Date.now();
    this.navigationSub = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.onNavigationActivity());
    this.zone.runOutsideAngular(() => {
      document.addEventListener('pointerdown', this.onUserActivity, true);
      document.addEventListener('keydown', this.onUserActivity, true);
      this.intervalId = setInterval(this.onTick, TICK_MS);
    });
  }

  stop(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    document.removeEventListener('pointerdown', this.onUserActivity, true);
    document.removeEventListener('keydown', this.onUserActivity, true);
    this.navigationSub?.unsubscribe();
    this.navigationSub = null;
    this.hideNotice();
    this.timeoutMs = null;
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private readClaimMinutes(): number | null {
    try {
      const jwt = this.authenticationService.getJWT();
      const token = jwt?.token;
      if (!token) return null;
      const decoded = this.jwtHelper.decodeToken(token);
      const raw = decoded?.[TIMEOUT_CLAIM];
      const minutes = Number(raw);
      return raw !== undefined && raw !== null && Number.isFinite(minutes) && minutes > 0 ? minutes : null;
    } catch {
      return null;
    }
  }

  private readStoredActivity(): number | null {
    try {
      const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
      const value = Number(raw);
      return raw && Number.isFinite(value) && value > 0 ? value : null;
    } catch {
      return null;
    }
  }

  private writeStoredActivity(value: number): void {
    if (this.storageUnavailable) return;
    try {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(value));
    } catch (error) {
      this.storageUnavailable = true;
      console.error('[idle] localStorage no disponible, se pierde la sincronización entre pestañas:', error);
    }
  }

  // Con el aviso abierto solo valen sus botones: cualquier tecla o clic lo descartaría sin que
  // el usuario se entere de que estuvo a punto de perder la sesión.
  private onUserActivity = (): void => {
    if (this.noticeRef) return;
    this.markActivity();
  };

  private markActivity(): void {
    const now = Date.now();
    this.lastActivityAt = now;
    this.writeStoredActivity(now);
    if (this.overlayRef) this.zone.run(() => this.hideNotice());
  }

  private onNavigationActivity(): void {
    if (this.noticeRef) return;
    if (Date.now() - this.armedAt < NAVIGATION_GRACE_MS) return;
    this.markActivity();
  }

  private onTick = (): void => {
    if (this.isLoggingOut || this.timeoutMs === null) return;

    const stored = this.readStoredActivity();
    const last = stored === null ? this.lastActivityAt : Math.max(this.lastActivityAt, stored);
    this.lastActivityAt = last;

    const idleMs = Date.now() - last;

    if (idleMs < 0) {
      this.markActivity();
      return;
    }
    if (idleMs >= this.timeoutMs) {
      this.zone.run(() => this.executeLogout(true));
      return;
    }

    const remainingMs = this.timeoutMs - idleMs;
    if (remainingMs > WARNING_MS) {
      if (this.overlayRef) this.zone.run(() => this.hideNotice());
      return;
    }

    const seconds = Math.max(1, Math.ceil(remainingMs / 1000));
    this.zone.run(() => this.showNotice(seconds));
  };

  private showNotice(seconds: number): void {
    let notice = this.noticeRef;
    if (!notice) {
      this.overlayRef = this.overlay.create({
        positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
        hasBackdrop: true,
        backdropClass: SESSION_OVERLAY_BACKDROP_CLASSES,
        panelClass: NOTICE_PANEL_CLASSES,
      });
      notice = this.noticeRef = this.overlayRef.attach(new ComponentPortal(IdleNoticeComponent, null, this.injector));
      notice.instance.stay.subscribe(() => this.markActivity());
      notice.instance.logoutNow.subscribe(() => this.executeLogout(false));
    }
    notice.setInput('secondsLeft', seconds);
  }

  private hideNotice(): void {
    this.overlayRef?.dispose();
    this.overlayRef = null;
    this.noticeRef = null;
  }

  private executeLogout(byInactivity: boolean): void {
    if (this.isLoggingOut) return;
    this.isLoggingOut = true;
    this.stop();

    if (!byInactivity) {
      this.sessionTermination.end(undefined, { revoke: true });
      return;
    }

    this.sessionTermination.begin({ revoke: true });
    this.modalService.closeAll();
    this.modalService
      .open(
        CoreLibSessionExpiredModalComponent,
        { data: { noCloseOnBackdropClick: true } },
        SESSION_OVERLAY_PANEL_CLASSES,
        SESSION_OVERLAY_BACKDROP_CLASSES,
      )
      .afterClosed()
      .subscribe(() => this.sessionTermination.end(LogoutReason.Idle));
  }
}
